import { useStatesQuery } from '@uplift/queries';
import { SettingsAddressForm, TextInput, Tooltip, YesNoRadioButtonGroup } from '@uplift/ui-v2';
import { useProfileStyles } from '../styles';
import { Box, IconButton } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import { ProfileForm } from './types';
import { getFormattedAddressParts } from '@uplift/utils';
import { useMemo } from 'react';

export type PracticeAddressFormProps = {
  index: number;
};

export const PracticeAddressForm: React.VFC<PracticeAddressFormProps> = props => {
  const { classes } = useProfileStyles();
  const formik = useFormikContext<ProfileForm['practiceAddress']>();
  const { data: states = [] } = useStatesQuery();

  const getFieldName = (name: keyof ProfileForm['practiceAddress']['addresses'][number]) => `addresses[${props.index}].${name}`;

  const publicProfileInfoTooltip = (
    <Tooltip placement="top" title='Your public profile only shows your city, state, and ZIP code. Selecting "Yes" will show your full address to clients after they have booked a session with you.'>
      <IconButton>
        <InfoOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
  );


  const displayStreetAddressHelperText = useMemo(() => {
    const values = formik.values.addresses[props.index];

    if ([ values?.address1, values?.city, values?.state, values?.zip ].every(Boolean)) {
      const { addressCityState, fullAddress } = getFormattedAddressParts(values, states.find(state => state.id === values.state), { stateName: 'shortName' });

      let address = '';

      if (values.displayStreetAddress) {
        address = fullAddress;
      } else {
        address = addressCityState;
      }

      return 'Ex: ' + address;
    }
  }, [ formik.values.addresses, props.index, states ]);

  return (
    <div className={classes.formGroup}>
      {props.index === 0 && (
        <TextInput
          name="practiceName"
          label="Practice Name (Optional)"
          inputClasses={{ root: classes.maxWidth400 }}
        />
      )}
      <SettingsAddressForm
        states={states}
        getFieldName={getFieldName}
      />
      <YesNoRadioButtonGroup
        name={getFieldName('displayStreetAddress')}
        label={<Box display="flex" alignItems="center" gap="0.5rem">Do you want to display your street address to your clients? {publicProfileInfoTooltip}</Box>}
        asBoolean
        helperText={displayStreetAddressHelperText}
      />
    </div>
  );
};
